(() => {
  if (window.MRLocationArtworkSearch) {
    window.MRLocationArtworkSearch.init(document);
    return;
  }

  const allowedTypes = new Set(['Mitch Revs Print', 'Mitch Revs Poster', 'Mitch Revs Original', 'Mitch Revs Sculpture']);
  const states = new WeakMap();
  const loaders = new Map();
  const stopWords = new Set(['a', 'an', 'and', 'at', 'for', 'in', 'of', 'the', 'to', 'please', 'art', 'artwork', 'create', 'paint']);
  let instance = 0;

  const normalise = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[’']/g, '').replace(/&/g, ' and ').replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
  const wordsOf = value => normalise(value).split(' ').filter(Boolean);
  const matchesWord = (words, term) => words.some(word => word.startsWith(term));
  const decodeText = value => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = String(value || '');
    return textarea.value;
  };

  function prepareProduct(product) {
    if (!product || !allowedTypes.has(product.type) || !product.id || !product.title) return null;
    const url = new URL(product.url, window.location.origin);
    if (url.origin !== window.location.origin || !/\/products\//.test(url.pathname)) return null;
    const title = normalise(product.title);
    const tags = normalise((Array.isArray(product.tags) ? product.tags : []).join(' '));
    const description = normalise(decodeText(product.description));
    return { ...product, url: url.href, titleText: title, tagsText: tags, descriptionText: description, titleWords: wordsOf(title), tagWords: wordsOf(tags), descriptionWords: wordsOf(description) };
  }

  function rankProducts(products, value) {
    const query = normalise(value);
    if (query.length < 2) return [];
    const rawTerms = wordsOf(query);
    const terms = rawTerms.filter(word => !stopWords.has(word));
    if (!terms.length) return [];
    const phrase = terms.join(' ');
    return products.map(product => {
      if (!terms.every(term => matchesWord(product.titleWords, term) || matchesWord(product.tagWords, term) || matchesWord(product.descriptionWords, term))) return null;
      let score = 0;
      if (product.titleText === query || product.titleText === phrase) score += 600;
      if (product.titleText.startsWith(phrase)) score += 90;
      if (product.titleText.includes(phrase)) score += 240;
      if (product.tagsText.includes(phrase)) score += 120;
      if (product.descriptionText.includes(phrase)) score += 25;
      terms.forEach(term => {
        if (matchesWord(product.titleWords, term)) score += 40;
        else if (matchesWord(product.tagWords, term)) score += 20;
        else score += 3;
      });
      return { product, score };
    }).filter(Boolean).sort((a, b) => b.score - a.score || a.product.title.localeCompare(b.product.title)).slice(0, 6).map(result => result.product);
  }

  async function fetchPage(url, page) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 10000);
    try {
      url.searchParams.set('page', String(page));
      const response = await fetch(url.href, { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Artwork index request failed');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const element = html.querySelector('[data-mr-artwork-index]');
      if (!element) throw new Error('Artwork index is missing');
      const data = JSON.parse(element.textContent);
      if (data.version !== 1 || data.collection !== 'all-art' || data.page !== page || !Array.isArray(data.products) || !Number.isInteger(data.pages) || data.pages < 1 || data.pages > 100) throw new Error('Artwork index is incomplete');
      if (data.next_page !== null && data.next_page !== page + 1) throw new Error('Artwork index pagination is invalid');
      return data;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  function loadProducts(root) {
    const url = new URL(root.dataset.artworkCollectionUrl, window.location.origin);
    if (url.origin !== window.location.origin || !url.pathname.endsWith('/collections/all-art')) return Promise.reject(new Error('Invalid artwork collection'));
    url.search = '';
    url.hash = '';
    url.searchParams.set('section_id', 'mr-location-artwork-index');
    const key = url.href;
    if (loaders.has(key)) return loaders.get(key);
    const promise = (async () => {
      const products = new Map();
      let page = 1;
      let pages = null;
      do {
        const data = await fetchPage(url, page);
        if (pages !== null && pages !== data.pages) throw new Error('Artwork catalogue changed while loading');
        pages = data.pages;
        data.products.forEach(product => {
          const prepared = prepareProduct(product);
          if (prepared) products.set(String(product.id), prepared);
        });
        if (data.next_page === null && page !== pages) throw new Error('Artwork index ended early');
        page = data.next_page;
      } while (page !== null);
      return Array.from(products.values());
    })();
    loaders.set(key, promise);
    promise.catch(() => loaders.delete(key));
    return promise;
  }

  function makeElement(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text) element.textContent = text;
    return element;
  }

  function attach(root, state) {
    const formRoot = root.querySelector('.globo-formbuilder[data-id="MTY2ODY0"], .globo-formbuilder[data-id="166864"], #globo-formbuilder-166864');
    const input = formRoot?.querySelector('[id="166864-text-1"], .mr-location-input input[type="text"], input.mr-location-input[type="text"]') || formRoot?.querySelector('input[type="text"]');
    if (!input || state.input === input) return;
    state.controller?.abort();
    state.panel?.remove();
    state.input = input;
    state.controller = new AbortController();
    const options = { signal: state.controller.signal };
    const strings = state.strings;
    const panel = makeElement('div', 'mr-location-artwork-results');
    panel.id = `MrLocationArtworkResults-${++instance}`;
    panel.hidden = true;
    const status = makeElement('p', 'mr-location-artwork-results__status');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    const list = makeElement('ul', 'mr-location-artwork-results__list');
    const note = makeElement('p', 'mr-location-artwork-results__note');
    const links = makeElement('div', 'mr-location-artwork-results__links');
    const browse = makeElement('a', '', strings.browse);
    browse.href = root.dataset.artworkCollectionUrl;
    const retry = makeElement('button', 'mr-location-artwork-results__retry', strings.retry);
    retry.type = 'button';
    retry.hidden = true;
    links.append(browse, retry);
    panel.append(status, list, note, links);
    const field = input.closest('.globo-form-control') || input.parentElement;
    field.append(panel);
    input.setAttribute('aria-controls', panel.id);
    state.panel = panel;
    let timer;
    let generation = 0;

    const clear = () => {
      generation += 1;
      window.clearTimeout(timer);
      panel.hidden = true;
      panel.removeAttribute('aria-busy');
      list.replaceChildren();
      status.textContent = '';
      note.textContent = '';
      retry.hidden = true;
    };

    const render = products => {
      list.replaceChildren();
      products.forEach(product => {
        const item = makeElement('li');
        const link = makeElement('a', 'mr-location-artwork-results__card');
        link.href = product.url;
        if (product.image) {
          const imageUrl = new URL(product.image, window.location.origin);
          if (imageUrl.protocol === 'https:' || imageUrl.origin === window.location.origin) {
            const image = makeElement('img', 'mr-location-artwork-results__image');
            image.src = imageUrl.href;
            image.alt = '';
            image.width = 72;
            image.height = 72;
            image.loading = 'lazy';
            link.append(image);
          }
        }
        const details = makeElement('span', 'mr-location-artwork-results__details');
        details.append(makeElement('span', 'mr-location-artwork-results__title', product.title), makeElement('span', 'mr-location-artwork-results__cta', strings.view));
        link.append(details);
        item.append(link);
        list.append(item);
      });
      status.textContent = products.length ? strings.found : strings.none;
      note.textContent = products.length ? strings.suggest : '';
    };

    const search = async () => {
      const value = input.value;
      if (normalise(value).length < 2) return clear();
      const current = ++generation;
      panel.hidden = false;
      panel.setAttribute('aria-busy', 'true');
      retry.hidden = true;
      status.textContent = strings.loading;
      note.textContent = '';
      list.replaceChildren();
      try {
        const products = await loadProducts(root);
        if (current !== generation || !input.isConnected || input.value !== value) return;
        render(rankProducts(products, value));
      } catch (error) {
        if (current !== generation || !input.isConnected || input.value !== value) return;
        status.textContent = strings.error;
        retry.hidden = false;
      } finally {
        if (current === generation) panel.removeAttribute('aria-busy');
      }
    };

    input.addEventListener('input', () => {
      generation += 1;
      window.clearTimeout(timer);
      if (normalise(input.value).length < 2) return clear();
      timer = window.setTimeout(search, 180);
    }, options);
    input.addEventListener('focus', () => {
      loadProducts(root).catch(() => {});
      if (normalise(input.value).length > 1 && panel.hidden) search();
    }, options);
    input.addEventListener('keydown', event => {
      if (event.key === 'Escape') clear();
    }, options);
    input.form?.addEventListener('reset', clear, options);
    retry.addEventListener('click', search, options);
    state.controller.signal.addEventListener('abort', clear, { once: true });
    if (normalise(input.value).length > 1) search();
  }

  function init(scope) {
    const roots = [...scope.querySelectorAll('.mr-location-suggestion[data-artwork-collection-url]')];
    if (scope.matches?.('.mr-location-suggestion[data-artwork-collection-url]')) roots.unshift(scope);
    roots.forEach(root => {
      if (states.has(root)) return;
      const config = root.querySelector('[data-mr-location-strings]');
      if (!config) return;
      let strings;
      try { strings = JSON.parse(config.textContent); } catch (error) { return; }
      const state = { strings };
      states.set(root, state);
      attach(root, state);
      state.observer = new MutationObserver(() => attach(root, state));
      state.observer.observe(root, { childList: true, subtree: true });
    });
  }

  window.MRLocationArtworkSearch = { init };
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('.mr-location-suggestion[data-artwork-collection-url]').forEach(root => {
      const state = states.get(root);
      state?.observer?.disconnect();
      state?.controller?.abort();
      states.delete(root);
    });
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
})();
